import dayjs from "dayjs";
import { horaDelCierre } from "../utils/numeroDeCajaDeBC";
import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import {
  fetchFromBusinessCentralPos,
  getCurrentEnvironment,
  postToBusinessCentralPos,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { getCountryData } from "../utils/countryUtils";
import { addMoney, resolveMoneyDecimals, roundMoney, sumMoney } from "../utils/money";
import { getAperturaDeCajaById } from "./aperturaDeCajaService";
import { getCajaByNoOrId } from "./cajaService";
import { getTPVByNoOrId } from "./tpvService";
import { enviarLote, resumenDelLote } from "./loteSyncHelpers";
import { anotarAviso, anotarError, anotarFila, anotarResumenDelLote } from "./syncLog";
import { getTurnoByNoOrId } from "./turnoService";
import { getPaymentLinesByDateTimeRange } from "./invoicePaymentService";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "cierres de caja";

// Resuelve la apertura/caja/TPV asociados al cierre y arma la clave compuesta
// ShiftNo/ShiftClosingNo = <No de la apertura que asignó BC>;HoraDeApertura(HH:mm)
const buildShiftContext = async (cierre) => {
  const apertura = await getAperturaDeCajaById(cierre.idApertura);
  if (!apertura) {
    throw new Error("No se pudo obtener la apertura de caja asociada al cierre");
  }

  const caja = await getCajaByNoOrId(apertura.Caja);
  if (!caja) {
    throw new Error("No se pudo obtener la caja asociada a la apertura");
  }
  if (!caja.No) {
    throw new Error("Falta sincronizar la caja con Business Central");
  }

  const tpv = await getTPVByNoOrId(caja.TPV);
  if (!tpv) {
    throw new Error("No se pudo obtener el TPV asociado a la caja");
  }
  if (!tpv.No) {
    throw new Error("Falta sincronizar el TPV con Business Central");
  }

  // El número del cierre ya no se compone acá.
  //
  // Se componía con la fecha formateada por el terminal --`DD/MM/YY`-- y tenía
  // que coincidir con el `No` que Business Central le había puesto a la
  // apertura. No siempre coincidía: BC arma ese número con el idioma de su
  // sesión y 8 de 60 aperturas salieron en `MM/DD/YY`. Para ésas, el prefijo
  // que mandaba el terminal no era el de su propia apertura.
  //
  // Ahora se antepone el `No` que BC asignó y que ya está guardado, sea cual
  // sea el formato en que lo escribió. Del resto de la cadena sólo se lee la
  // hora, en la última posición -- acuerdo del recorrido de la caja 3, §6.
  if (!apertura.No) {
    // Sin ese número no hay prefijo posible: el cierre no se manda y queda
    // pendiente con el motivo, en vez de viajar con un `undefined` delante.
    throw new Error(
      "La apertura de este cierre todavía no tiene número de Business Central: sincronízala primero."
    );
  }

  const horaApertura = apertura.HoraDeApertura || "00:00";
  const shiftNo = `${apertura.No};${horaApertura}`;

  const turno = await getTurnoByNoOrId(apertura.Turno);

  const env = await getCurrentEnvironment();
  const country = getCountryData(env.country);
  const currency = country?.currency || "USD";

  return { apertura, caja, tpv, turno, shiftNo, currency, country };
};

/**
 * ¿Publica ya Business Central la hora del cierre?
 *
 * `PostingTime` entra en la extensión 1.0.4.55 --tarea 5 del acuerdo del
 * recorrido de la caja 3-- y hoy no está. Mandarlo antes no es inocuo: el POST
 * entero se rechaza con «property does not exist» y **el cierre no se escribe**.
 * Medido contra el ambiente el 27/08/2026.
 *
 * Por eso se pregunta en vez de suponer: una lectura por sesión, y el campo
 * empieza a viajar solo el día que BC lo publique, sin esperar a otra versión
 * del terminal. Si la consulta falla o no devuelve ningún cierre, no se manda:
 * la fecha --que sí existe hoy-- ya arregla lo que este cambio venía a arreglar.
 */
//
// **Actualización del 12/09/2026: ya no se pregunta con una muestra.** La muestra
// tenía un hueco que no se veía: el primer cierre de un ambiente no encontraba
// ninguno, la pregunta contestaba que no, y ese cierre viajaba sin hora.
// Business Central le ponía la de su reloj, en UTC. Le pasó al primer cierre de
// Costa Rica producción, `TIENDA_04;TPV_04;11/09/26;14:51`, que guarda
// `21:29:47`.
//
// `PostingTime` lo publican hoy los tres ambientes de Costa Rica --medido en su
// `$metadata`-- y BC lo conserva al segundo cuando llega. Así que se manda de
// entrada, y si un ambiente anterior a la 1.0.4.55 lo rechaza,
// `syncCierreDeCajaToBC` repite una vez sin él y la sesión lo recuerda. Acuerdo
// `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H3.
let publicaHoraDeCierre = true;

const aceptaHoraDeCierre = async () => publicaHoraDeCierre;

/** Si Business Central rechazó la cabecera por la hora, y no por otra cosa. */
export const esRechazoPorLaHora = (error) =>
  error?.response?.status === 400 &&
  /PostingTime/i.test(String(error?.response?.data?.error?.message ?? error?.message ?? ""));

/** Vuelve a lo que se supone al arrancar la sesión. Lo usan las pruebas. */
export const olvidarLoAprendidoDeLaHora = () => {
  publicaHoraDeCierre = true;
};

// Escapa comillas simples para armar filtros OData ($filter) de forma segura
const escapeODataString = (value) => String(value ?? "").replace(/'/g, "''");

const parseCuadreMetodosPago = (cierre) => {
  if (!cierre.CuadreMetodosPago) return [];
  try {
    return JSON.parse(cierre.CuadreMetodosPago) || [];
  } catch (error) {
    anotarError(ENTIDAD, "no se pudo interpretar el cuadre por forma de pago", error);
    return [];
  }
};

/**
 * El primero de los candidatos que sea un número de verdad.
 *
 * `Number("")` es 0 y `Number(null)` también, así que no sirve para decidir si
 * un valor está o no está: acá una cadena vacía, un nulo o un texto que no es
 * número se descartan, y sólo se acepta lo que Business Central puede leer como
 * entero.
 *
 * @returns {number|null}
 */
export const primerNumero = (...candidatos) => {
  for (const valor of candidatos) {
    if (valor === null || valor === undefined) continue;
    const texto = String(valor).trim();
    if (!texto) continue;
    const numero = Number(texto);
    if (Number.isFinite(numero)) return numero;
  }

  return null;
};

// Cabecera del cierre (ShiftsClosing).
//
// Exportada para poder probarla, igual que buildShiftsClosingLines: acá se
// decide con qué fecha queda registrado el turno.
export const buildShiftsClosingHeader = (
  cierre,
  apertura,
  caja,
  turno,
  shiftNo,
  cuadreMetodosPago,
  country = null,
  aceptaHora = false
) => {
  const decimals = resolveMoneyDecimals(country);
  const amountTotal = sumMoney(cuadreMetodosPago, (item) => item.cobro, decimals);

  // El número de turno, y sólo si es un número.
  //
  // Business Central declara `ShiftNo` como Edm.Int32. Antes se mandaba
  // `turno?.No || ""`, y una cadena vacía no es un entero: el POST entero se
  // rechaza con «Cannot convert the literal '' to the expected type
  // 'Edm.Int32'» y el cierre no se escribe. Le pasó a Costa Rica el 28 de
  // agosto de 2026, con una apertura cuyo turno no estaba en el maestro local.
  //
  // El valor bueno es el que lleva la propia apertura --es el que BC conoce--;
  // el maestro de turnos sólo aporta la descripción, y puede no estar
  // sincronizado. Si no hay ninguno que sea un número, la clave no viaja:
  // Business Central distingue una clave ausente de una que no puede
  // interpretar, igual que con `Hora_creacion` en la cabecera de la factura.
  const numeroDeTurno = primerNumero(apertura?.Turno, turno?.No);

  return {
    ShiftClosingNo: shiftNo,
    ...(numeroDeTurno === null ? {} : { ShiftNo: numeroDeTurno }),
    RegisterID: caja.No,
    AmountTotal: amountTotal,
    UserId: cierre.Cajero || apertura.Cajero || "",
    initialAmount: roundMoney(cierre.ImporteDeApertura, decimals),
    // La fecha del turno, que hasta ahora no se mandaba.
    //
    // Sin ella Business Central le ponía la de su propio reloj, y 18 de 57
    // cierres quedaron fechados de 1 a 12 días después de su turno. Como el
    // informe y Traer Históricos buscan el cierre por este campo, el cierre de
    // un turno no aparecía el día del turno.
    //
    // Va la fecha de APERTURA y no la de cierre, que es la que ya usan las dos
    // cosas que dependen de ella: el filtro del pull y el emparejamiento
    // cierre-apertura -- acuerdo del recorrido de la caja 3, §2.
    PostingDate: dayjs(apertura.FechaDeApertura).format("YYYY-MM-DD"),
    // La hora real del cierre, tomada al guardarlo.
    //
    // Sólo se manda si Business Central publica el campo y el cierre lo tiene:
    // los anteriores a este cambio no lo guardaron.
    ...(aceptaHora && cierre.HoraDeCierre ? { PostingTime: cierre.HoraDeCierre } : {}),
  };
};

// Obtiene las líneas de pago (facturas/notas de crédito) del período del cierre.
// Se consulta una sola vez y se reutiliza tanto para armar las líneas por forma de pago
// como el detalle de documentos, evitando recalcular/consultar dos veces lo mismo.
// Exportada para que CierreDeCajaList.js pueda recalcular el detalle de un cierre
// puntual sin duplicar esta lógica de armar el rango apertura->cierre.
export const getPagosDelCierre = async (cierre, apertura, country = null) => {
  const fechaApertura = dayjs(apertura.FechaDeApertura).format("YYYY-MM-DD");
  const horaApertura = apertura.HoraDeApertura || "00:00";
  const aperturaDateTime = dayjs(`${fechaApertura} ${horaApertura}`, "YYYY-MM-DD HH:mm").toDate();

  const cierreDateTime = cierre.FechaDeCierre
    ? dayjs(cierre.FechaDeCierre).endOf("day").toDate()
    : null;

  return getPaymentLinesByDateTimeRange(
    aperturaDateTime,
    cierreDateTime,
    !!cierre.CierreHistorico,
    country
  );
};

// Empareja cierres importados sin idApertura (Traer Histórico) con la
// apertura local más cercana: misma Caja, mismo día, HoraDeApertura <=
// hora del cierre (una apertura no puede ser posterior a su cierre), la más
// próxima posible entre las candidatas, y que no haya sido usada ya por otro
// cierre de esta misma pasada. Los cierres se procesan del más temprano al
// más tardío para que, entre dos cierres del mismo día, el más cercano a una
// apertura dada se la quede antes que uno más lejano.
// La hora del cierre no se guarda en un campo propio (no existe HoraDeCierre
// en el schema) -- se extrae del No (formato Tienda;TPV;Fecha;Hora, ej.
// "MALL DEL SOL;PVMDSC3;07/08/26;17:15", el mismo que arma
// fetchAndMapCierresDeCaja en useSyncHistoricos.js).
// Devuelve un Map cierre.id -> apertura (sin entrada si no se encontró pareja).
export const matchAperturasToCierres = (cierres, aperturas) => {
  const matches = new Map();
  const usedAperturaIds = new Set();

  const parseHoraDeCierre = (cierre) => horaDelCierre(cierre.No);

  const pendientes = cierres
    .filter((c) => !c.idApertura && c.FechaDeCierre && c.Caja)
    .map((c) => ({ cierre: c, hora: parseHoraDeCierre(c) }))
    .filter((c) => c.hora)
    .sort((a, b) => {
      const fa = `${dayjs(a.cierre.FechaDeCierre).format("YYYY-MM-DD")} ${a.hora}`;
      const fb = `${dayjs(b.cierre.FechaDeCierre).format("YYYY-MM-DD")} ${b.hora}`;
      return fa.localeCompare(fb);
    });

  pendientes.forEach(({ cierre, hora: horaCierre }) => {
    const fechaCierre = dayjs(cierre.FechaDeCierre).format("YYYY-MM-DD");
    const [hC, mC] = horaCierre.split(":").map(Number);
    const minutosCierre = hC * 60 + (mC || 0);

    let mejor = null;
    let mejorMinutos = -1;

    aperturas.forEach((apertura) => {
      if (usedAperturaIds.has(apertura.id)) return;
      if (apertura.Caja !== cierre.Caja) return;
      if (!apertura.FechaDeApertura) return;
      if (dayjs(apertura.FechaDeApertura).format("YYYY-MM-DD") !== fechaCierre) return;

      const horaApertura = apertura.HoraDeApertura || "00:00";
      const [hA, mA] = horaApertura.split(":").map(Number);
      const minutosApertura = hA * 60 + (mA || 0);

      if (minutosApertura > minutosCierre) return;
      if (minutosApertura > mejorMinutos) {
        mejor = apertura;
        mejorMinutos = minutosApertura;
      }
    });

    if (mejor) {
      usedAperturaIds.add(mejor.id);
      matches.set(cierre.id, mejor);
    }
  });

  return matches;
};

/**
 * Líneas del cierre por forma de pago (Shift_Closing_Lines).
 *
 * **Una línea por forma de pago y divisa**, que es exactamente la clave de la
 * tabla en Business Central. Antes se armaba una línea por forma de pago Y tipo
 * de documento, así que cuando una forma de pago tenía factura y nota de
 * crédito, la segunda línea chocaba contra la primera: BC hacía `Insert()` y se
 * saltaba el fallo en silencio, de modo que **el importe de la nota de crédito
 * desaparecía del arqueo sin dejar rastro**.
 *
 * Se verificó contra el entorno: de siete cierres con los dos tipos de documento
 * en la misma forma de pago, en los siete la línea guardaba sólo la suma de las
 * facturas. En `MALL DEL SOL;PVMDSC5;14/08/26;10:46` la línea de TJCTO decía
 * 324,89 y el conteo del cajero 146,88 -- la diferencia son las dos notas de
 * crédito que nunca entraron. Neteadas, la línea da 146,88: coincide con lo
 * digitado y con la suma del detalle.
 *
 * El desglose por tipo de documento no se pierde: vive en
 * `Shift_Closing_Lines_Detail`, que sí lo lleva en su clave.
 *
 * El monto esperado (Amount) es el neto de la forma de pago -- los pagos ya
 * traen el signo, negativo en las notas de crédito--; a la línea que se lleva
 * el fondo de apertura (`esFondoDeApertura`, decidido una sola vez en
 * `resumenDelTurno.js` y guardado en el `CuadreMetodosPago` del cierre) además
 * se le suma el importe de apertura, igual que hace el formulario al calcular
 * el "cobro" esperado.
 *
 * **Antes se decidía acá por el código, literal "EFECTIVO"** -- se dejó de
 * alinear con la pantalla el día que una tienda cobra en efectivo con otro
 * código (Perú: `EFE-1310`, etc.): la pantalla sumaba el fondo en esa fila y lo
 * mandado a BC no lo sumaba en ninguna. Un cierre guardado antes del
 * 23/09/2026 no trae `esFondoDeApertura` en su `CuadreMetodosPago`: para ésos
 * se cae al criterio viejo, así no se reescribe la aritmética de un cierre que
 * ya se armó (y puede que ya se haya sincronizado) con el otro criterio.
 *
 * Exportada para poder probarla: es la lógica del arqueo que se manda a BC.
 */
export const buildShiftsClosingLines = (
  shiftNo,
  currency,
  cuadreMetodosPago,
  pagos,
  importeApertura,
  country = null
) => {
  const decimals = resolveMoneyDecimals(country);
  const netoPorMetodo = {};
  pagos.forEach((pago) => {
    const metodo = (pago.paymentMethodCode || "").toUpperCase();
    netoPorMetodo[metodo] = addMoney(netoPorMetodo[metodo] || 0, pago.Amount || 0, decimals);
  });

  return cuadreMetodosPago.map((item) => {
    const metodo = (item.codigoMetodoPago || "").toUpperCase();
    const esEfectivo = item.esFondoDeApertura ?? metodo === "EFECTIVO";
    // Una forma de pago sin documentos en el período igual se reporta, para no
    // perder el digitado y la diferencia que ingresó el cajero.
    const neto = netoPorMetodo[metodo] || 0;

    const line = {
      Shift_Closing_No: shiftNo,
      Payment_Method: item.codigoMetodoPago,
      Currency_Code: currency,
      Amount: esEfectivo ? addMoney(neto, importeApertura, decimals) : neto,
      Amount_Entered: roundMoney(item.digitado, decimals),
      // La diferencia NO se manda. Business Central la deriva de estos dos
      // campos, y es la misma resta que hace el formulario --digitado menos
      // cobro--, así que mandarla sólo podía introducir divergencia.
      //
      // BC había observado que el valor del terminal no coincidía con esa resta:
      // era un síntoma del choque de líneas, no una discrepancia de criterio.
      // Con las notas de crédito perdidas, el Amount que llegaba era el bruto de
      // las facturas y la resta daba cualquier cosa. Neteado, coinciden.
      Detail: JSON.stringify(
        (item.detalle || []).map((detalle) => ({
          Code: detalle.codigo,
          Amount: roundMoney(detalle.total, decimals),
        }))
      ),
    };

    // Filtrar campos con valores vacíos, igual que el resto de las integraciones
    // custom de Importaciones_* (BC rechaza el request si vienen como "")
    return Object.fromEntries(Object.entries(line).filter(([, value]) => value !== ""));
  });
};

// Detalle de documentos por línea de forma de pago (Shift_Closing_Lines_Detail).
//
// Sólo viajan las ventas que tienen número de Business Central, y ese número es
// el `Document_No`: `No` o `Pre_Assigned_No` (`pago.numeroDeBC`). Antes viajaba
// `invoiceNo`, que para una venta que nunca salió del terminal --por ejemplo,
// fuera de la cola por `DATOS_INVALIDOS` o `SIN_CORREO`-- es el id de Dexie o
// «Desconocido». Desde la extensión 1.0.4.112, BC rechaza ese renglón con
// `DOCUMENTO_NO_EXISTE`, que se reintenta, y el cierre se habría quedado
// pendiente para siempre. Esas ventas se avisan aparte (ventasSinNumeroDeBC).
// Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H4 · TPV.
//
// Exportada para poder probarla.
export const buildShiftsClosingLinesDetails = (shiftNo, currency, pagos, country = null) =>
  pagos
    .filter((pago) => pago.numeroDeBC)
    .map((pago) => {
      const detail = {
        Shift_Closing_No: shiftNo,
        Payment_Method: pago.paymentMethodCode,
        Document_Type: pago.documentType || "Invoice",
        Document_No: pago.numeroDeBC,
        Currency_Code: currency,
        Amount: roundMoney(pago.Amount, resolveMoneyDecimals(country)),
      };
      // Filtrar campos con valores vacíos, igual que el resto de las integraciones
      // custom de Importaciones_* (BC rechaza el request si vienen como "")
      return Object.fromEntries(Object.entries(detail).filter(([, value]) => value !== ""));
    });

/**
 * Las ventas del cierre que no llegaron a Business Central, una vez cada una
 * aunque se hayan cobrado con varias formas de pago. Se nombran con `invoiceNo`,
 * que es como aparecen en la columna «Factura» del cierre.
 */
export const ventasSinNumeroDeBC = (pagos) => [
  ...new Set(pagos.filter((pago) => !pago.numeroDeBC).map((pago) => pago.invoiceNo)),
];

export const avisoDeVentaSinNumero = (venta) =>
  `La venta ${venta} no llegó a Business Central; no va en el detalle del cierre.`;

/**
 * Manda un bloque del cierre --las líneas o el detalle-- y devuelve los motivos
 * de los renglones que Business Central rechazó.
 *
 * Hasta ahora estas dos llamadas se hacían con `await` **sin recoger el valor**:
 * las acciones devolvían `'Ok'` o el texto del error dentro de un 200, así que un
 * fallo era invisible por partida doble -- BC no escribía la línea y el cliente
 * marcaba el cierre como sincronizado igual--. Desde el acuerdo 10 las dos
 * responden con el envoltorio `Results`, un renglón por registro y en el mismo
 * orden en que se enviaron, así que el rechazo ya se puede leer y atribuir.
 *
 * @param {(registro: object) => string} describir - Cómo nombrar el renglón en el motivo.
 */
const enviarBloqueDelCierre = async (accion, clave, registros, etiqueta, describir) => {
  const { results } = await enviarLote(
    accion,
    { data: JSON.stringify({ [clave]: registros }) },
    registros.length,
    etiqueta
  );

  return results
    .map((resultado, indice) =>
      resultado.ok
        ? null
        : `${describir(registros[indice])}: ${
            resultado.message || "rechazado por Business Central"
          }`
    )
    .filter(Boolean);
};

// Sincronizar un cierre de caja a Business Central
export const syncCierreDeCajaToBC = async (cierre) => {
  try {
    const { apertura, caja, turno, shiftNo, currency, country } = await buildShiftContext(cierre);
    const cuadreMetodosPago = parseCuadreMetodosPago(cierre);
    const shiftNoFilter = `ShiftClosingNo eq '${escapeODataString(shiftNo)}'`;

    // 1. Enviar la cabecera del cierre, sólo si no existe ya en BC (evita duplicados en reintentos)
    const existingHeaders = await fetchFromBusinessCentralPos("ShiftsClosing", shiftNoFilter);
    if (!existingHeaders || existingHeaders.length === 0) {
      const armarCabecera = async () =>
        buildShiftsClosingHeader(
          cierre,
          apertura,
          caja,
          turno,
          shiftNo,
          cuadreMetodosPago,
          country,
          await aceptaHoraDeCierre()
        );

      try {
        await postToBusinessCentralPos("ShiftsClosing", await armarCabecera());
      } catch (error) {
        // Un ambiente que no publica la hora rechaza la cabecera entera por ella.
        // Se repite una sola vez sin el campo, y no se vuelve a mandar en esta
        // sesión. Cualquier otro rechazo sigue su camino.
        if (!publicaHoraDeCierre || !esRechazoPorLaHora(error)) throw error;

        publicaHoraDeCierre = false;
        anotarAviso(
          ENTIDAD,
          "Business Central rechazó la hora del cierre: se reenvía sin ella y no se vuelve a mandar en esta sesión"
        );
        await postToBusinessCentralPos("ShiftsClosing", await armarCabecera());
      }
    }

    // Pagos del período: se consultan una sola vez y se reutilizan para líneas y detalle
    const pagos = await getPagosDelCierre(cierre, apertura, country);

    // Las ventas sin número de Business Central no van en el detalle (H4). Se
    // avisa aquí, antes de mandar nada, para que el aviso quede en la consola
    // aunque el cierre se corte más adelante. Las líneas por forma de pago sí
    // las cuentan: el dinero se cobró igual.
    const avisos = ventasSinNumeroDeBC(pagos).map(avisoDeVentaSinNumero);
    avisos.forEach((aviso) => anotarAviso(ENTIDAD, `${shiftNo} — ${aviso}`));

    // 2. Enviar TODAS las líneas por forma de pago, juntas en un solo envío
    // (Importaciones.InsertShiftClosingLine).
    //
    // No se saltean las que ya existen. Desde la extensión 1.0.4.38 la acción
    // actualiza la línea al reenviarla, así que **una corrección del arqueo sí
    // llega a Business Central** -- antes se quedaba en el terminal para
    // siempre. El rechazo por clave duplicada sólo salta cuando llegan dos
    // líneas con la misma clave dentro de la MISMA petición, y eso ya no puede
    // pasar: se manda una por forma de pago y divisa.
    //
    // De paso desaparece una consulta por cierre: ya no hace falta preguntar qué
    // líneas existen.
    const lineasAEnviar = buildShiftsClosingLines(
      shiftNo,
      currency,
      cuadreMetodosPago,
      pagos,
      cierre.ImporteDeApertura,
      country
    );
    if (lineasAEnviar.length > 0) {
      const rechazos = await enviarBloqueDelCierre(
        "Importaciones_InsertShiftClosingLine",
        "Shift_Closing_Lines",
        lineasAEnviar,
        "líneas del cierre de caja",
        (line) => line.Payment_Method
      );
      // El detalle cuelga de la línea, así que si una línea no entró no tiene
      // sentido mandarle sus documentos detrás: se corta acá y el cierre queda
      // pendiente con el motivo.
      if (rechazos.length > 0) {
        throw new Error(`Business Central rechazó líneas del cierre -- ${rechazos.join("; ")}`);
      }
    }

    // 3. Enviar el detalle de documentos (facturas/notas de crédito) que aún no exista,
    // todos juntos en un solo envío (Importaciones.InsertShiftClosingLineDetail)
    const existingDetails = await fetchFromBusinessCentralPos(
      "ShiftsClosingLinesDetails",
      shiftNoFilter
    );
    const existingDetailKeys = new Set(
      (existingDetails || []).map(
        (detail) => `${(detail.PaymentMethod || "").toUpperCase()}|${detail.DocumentNo || ""}`
      )
    );
    const lineDetails = buildShiftsClosingLinesDetails(shiftNo, currency, pagos, country);
    const detallesAEnviar = lineDetails.filter(
      (detail) =>
        !existingDetailKeys.has(
          `${(detail.Payment_Method || "").toUpperCase()}|${detail.Document_No || ""}`
        )
    );
    if (detallesAEnviar.length > 0) {
      const rechazos = await enviarBloqueDelCierre(
        "Importaciones_InsertShiftClosingLineDetail",
        "Shift_Closing_Lines_Detail",
        detallesAEnviar,
        "detalle del cierre de caja",
        (detail) => `${detail.Payment_Method} ${detail.Document_No || ""}`.trim()
      );
      if (rechazos.length > 0) {
        throw new Error(`Business Central rechazó el detalle del cierre -- ${rechazos.join("; ")}`);
      }
    }

    // 4. Marcar el cierre local como sincronizado
    await db.cierreDeCaja.update(cierre.id, {
      No: shiftNo,
      // Un cierre que vuelve a salir bien deja de arrastrar el motivo del intento
      // anterior. Lo lee RecordSyncTag para marcarlo en rojo hasta entonces.
      syncError: null,
      // Backfill para cierres locales creados antes de que se empezara a guardar
      // el Turno en addCierreDeCaja -- así se autocorrigen en el próximo sync.
      Turno: cierre.Turno || turno?.No || apertura?.Turno || null,
      lastSynced: getCurrentDateTimeISO(),
      pendingSync: 0,
    });

    // Los avisos van en el mensaje, que es lo que pinta el cuadro de resultados
    // de syncCierresDeCaja, y además aparte, para quien quiera mostrarlos solos.
    return {
      success: true,
      message: avisos.length
        ? `Cierre de caja sincronizado correctamente. ${avisos.join(" ")}`
        : "Cierre de caja sincronizado correctamente",
      avisos,
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío (id ${cierre.id})`, error);

    // Business Central rechaza con el motivo dentro de
    // error.response.data.error.message; `error.message` a secas sería sólo
    // "Request failed with status code 400", que no le dice nada a nadie.
    const motivo = formatAxiosError(error, "No se pudo sincronizar el cierre de caja");

    // El motivo queda en el propio registro: es lo único que sobrevive a la
    // corrida, y es lo que permite que el listado explique por qué está en rojo
    // en vez de limitarse a decir que sigue pendiente.
    try {
      await db.cierreDeCaja.update(cierre.id, { syncError: motivo });
    } catch (errorAlGuardar) {
      anotarError(ENTIDAD, "no se pudo guardar el motivo del fallo", errorAlGuardar);
    }

    return { success: false, message: motivo };
  }
};

// Sincronizar todos los cierres de caja pendientes
// Nombre con el que se identifica un cierre en el cuadro de resultados. El No lo
// asigna BC al sincronizar, así que un cierre pendiente todavía no lo tiene: se
// cae al cajero y la fecha, que es como se reconoce en el listado.
const nombreDeCierre = (cierre) =>
  cierre.No ||
  [cierre.Cajero, cierre.FechaDeCierre].filter(Boolean).join(" · ") ||
  `Cierre ${cierre.id}`;

/**
 * Sincroniza los cierres pendientes, uno por uno.
 *
 * A diferencia de los seis maestros, los cierres se recorren de a uno: cada uno
 * necesita su propia cabecera por la API REST de punto de venta y su propia
 * comprobación de qué líneas y qué documentos existen ya en BC. Lo que sí viaja
 * por lote es lo de adentro -- todas las líneas de un cierre en una llamada, y
 * todo su detalle en otra--, y desde el acuerdo 10 las dos devuelven el
 * envoltorio `Results`, así que un rechazo deja el cierre pendiente con su
 * motivo en vez de darse por bueno.
 *
 * El resultado se devuelve con la misma forma `lote` que el resto de los
 * maestros, para que la pantalla muestre el mismo cuadro.
 *
 * Se conserva el array `results` porque lo consume syncAfterCierreDeCaja.
 */
export const syncCierresDeCaja = async () => {
  try {
    // Obtener cierres pendientes de sincronización
    const pendingCierres = await db.cierreDeCaja.where("pendingSync").equals(1).toArray();

    const results = [];
    const detalle = [];

    // Sincronizar cada cierre pendiente
    for (const cierre of pendingCierres) {
      const result = await syncCierreDeCajaToBC(cierre);

      results.push({
        id: cierre.id,
        No: cierre.No,
        success: result.success,
        message: result.message,
        avisos: result.avisos || [],
      });

      // El No lo asigna BC al sincronizar, así que se relee para mostrarlo.
      const guardado = result.success ? await db.cierreDeCaja.get(cierre.id) : null;

      detalle.push(
        anotarFila(
          ENTIDAD,
          {
            registro: nombreDeCierre(cierre),
            no: guardado?.No || cierre.No || null,
            accion: result.success ? "created" : "error",
            ok: result.success,
            mensaje: result.message,
          },
          cierre.id
        )
      );
    }

    results.lote = resumenDelLote(detalle);
    anotarResumenDelLote(ENTIDAD, results.lote);

    return results;
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    throw error;
  }
};
